import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import PractitionerSearch from '@/components/admin/PractitionerSearch';

interface FPRow {
  id: string;
  practitioner_id: string;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
  practitioner?: {
    first_name: string;
    last_name: string;
    professional_title: string;
    avatar_url: string;
  };
}

export default function AdminFeaturedPractitioners() {
  const { toast } = useToast();
  const [rows, setRows] = useState<FPRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSearch, setShowSearch] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('featured_practitioners')
        .select(`
          *,
          practitioner:practitioners(
            first_name,
            last_name,
            professional_title,
            avatar_url
          )
        `)
        .order('sort_order', { ascending: true });
      if (error) throw error;
      setRows((data || []) as FPRow[]);
    } catch (e) {
      console.error('Load featured error:', e);
      toast({ variant: 'destructive', title: '加载失败', description: '请稍后再试。' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSelectPractitioner = async (practitioner: any) => {
    try {
      const maxOrder = rows.reduce((m, r) => Math.max(m, r.sort_order), 0) + 1;
      const { error } = await supabase
        .from('featured_practitioners')
        .insert({ practitioner_id: practitioner.id, is_active: true, sort_order: maxOrder });
      if (error) throw error;
      toast({ title: '已添加', description: `已将 ${practitioner.first_name} ${practitioner.last_name} 添加为精选 practitioner` });
      setShowSearch(false);
      await load();
    } catch (e: any) {
      toast({ variant: 'destructive', title: '添加失败', description: e?.message || '该 practitioner 可能已被添加' });
    }
  };

  const handleToggleActive = async (row: FPRow) => {
    try {
      const { error } = await supabase
        .from('featured_practitioners')
        .update({ is_active: !row.is_active })
        .eq('id', row.id);
      if (error) throw error;
      await load();
    } catch (e) {
      toast({ variant: 'destructive', title: '更新失败' });
    }
  };

  const handleDelete = async (row: FPRow) => {
    try {
      const { error } = await supabase
        .from('featured_practitioners')
        .delete()
        .eq('id', row.id);
      if (error) throw error;
      await load();
    } catch (e) {
      toast({ variant: 'destructive', title: '删除失败' });
    }
  };

  const move = async (row: FPRow, dir: -1 | 1) => {
    const targetIdx = rows.findIndex(r => r.id === row.id) + dir;
    if (targetIdx < 0 || targetIdx >= rows.length) return;
    const other = rows[targetIdx];
    try {
      const updates = [
        supabase.from('featured_practitioners').update({ sort_order: other.sort_order }).eq('id', row.id),
        supabase.from('featured_practitioners').update({ sort_order: row.sort_order }).eq('id', other.id),
      ];
      const [{ error: e1 }, { error: e2 }] = await Promise.all(updates);
      if (e1 || e2) throw e1 || e2;
      await load();
    } catch (e) {
      toast({ variant: 'destructive', title: '排序失败' });
    }
  };

  const excludeIds = rows.map(r => r.practitioner_id);
  const getPractitionerName = (row: FPRow) => {
    if (row.practitioner) {
      return `${row.practitioner.first_name || ''} ${row.practitioner.last_name || ''}`.trim() || 'Unknown';
    }
    return row.practitioner_id;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Featured Practitioners</h2>
        <Button onClick={() => setShowSearch(!showSearch)}>
          <Plus className="w-4 h-4 mr-1"/>
          {showSearch ? '取消' : '添加 Practitioner'}
        </Button>
      </div>

      {showSearch && (
        <Card>
          <CardHeader>
            <CardTitle>搜索并添加 Practitioner</CardTitle>
          </CardHeader>
          <CardContent>
            <PractitionerSearch 
              onSelectPractitioner={handleSelectPractitioner}
              excludeIds={excludeIds}
            />
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>List</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order</TableHead>
                  <TableHead>Practitioner</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r, idx) => (
                  <TableRow key={r.id}>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="outline" size="icon" onClick={() => move(r, -1)} disabled={idx === 0}><ChevronUp className="w-4 h-4"/></Button>
                        <Button variant="outline" size="icon" onClick={() => move(r, 1)} disabled={idx === rows.length-1}><ChevronDown className="w-4 h-4"/></Button>
                        <Badge variant="secondary">{r.sort_order}</Badge>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">{getPractitionerName(r)}</div>
                        {r.practitioner?.professional_title && (
                          <div className="text-sm text-muted-foreground">{r.practitioner.professional_title}</div>
                        )}
                        <div className="text-xs text-muted-foreground font-mono">{r.practitioner_id}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={r.is_active ? 'secondary' : 'outline'}>{r.is_active ? 'Active' : 'Inactive'}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleToggleActive(r)}>
                          {r.is_active ? 'Deactivate' : 'Activate'}
                        </Button>
                        <Button variant="destructive" size="sm" onClick={() => handleDelete(r)}>
                          <Trash2 className="w-4 h-4 mr-1"/>Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
